'use client'

import { AlertCircleIcon, CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import type { WorkspaceSubscription } from '@/lib/types'
import { SpacingVertical } from '../ui/spacing'

interface PaymentIssueBannerProps {
  subscription: WorkspaceSubscription
}

/**
 * Warning banner displayed when subscription has payment issues (past_due/unpaid status)
 */
export function PaymentIssueBanner({ subscription }: PaymentIssueBannerProps) {
  const t = useTranslations('pricing-v3.payment_issue_banner')
  const [loading, setLoading] = useState(false)

  const handleUpdatePayment = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/stripe/billing-portal?returnJson=true')
      if (!res.ok) {
        throw new Error('Failed to fetch billing portal URL')
      }
      const data = await res.json()
      if (data.url) {
        // Open billing portal focused on payment methods section
        window.open(`${data.url}#payment-methods`, '_blank')
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error)
      alert(t('error_opening_portal'))
    } finally {
      setLoading(false)
    }
  }

  const isUnpaid = subscription.status === 'unpaid'

  const title = isUnpaid ? t('title_unpaid') : t('title_past_due')
  const description = isUnpaid
    ? t('description_unpaid')
    : t('description_past_due')

  return (
    <div className="container pb-6 mx-auto">
      <Alert variant={'default'} className="max-w-md mx-auto ">
        <AlertCircleIcon />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          <div className="flex-1">
            <p className="text-sm mb-2">{description}</p>
            <p className="text-xs opacity-80">
              {t('subscription_label')}: {subscription.plan.toUpperCase()} •{' '}
              {subscription.credits_per_month} {t('credits_per_month')} •{' '}
              {t('status_label')}:{' '}
              <span className="font-semibold uppercase">
                {subscription.status}
              </span>
            </p>
          </div>
        </AlertDescription>
        <SpacingVertical space={6} />
        <div className="flex justify-end">
          <Button
            onClick={handleUpdatePayment}
            disabled={loading}
            size="sm"
            variant={'destructive'}
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {loading ? t('opening') : t('update_payment_button')}
          </Button>
        </div>
      </Alert>
    </div>
  )
}
